import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Alert, Spin } from 'antd';
import { signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import { clearLocalStorage } from '@src/appRedux/sagas/Auth';
import { FSCollections } from '@src/constants/database';
import { auth, db } from '@src/firebase/firebase';
import { getOrganizationCountry } from '@src/packages/utils/nationalId';
import AuthService from '@src/services/AuthService';

import Card from 'containers/App/Auth/components/Card';

import { simpleDecode } from 'packages/utils';

import IntlMessages from 'util/IntlMessages';
import safeExecute from 'util/safeExecute';

import ConfirmUserForm from '../../components/ConfirmUserForm';

const AcceptInvitation = () => {
  const intl = useIntl();
  const { token } = useParams();

  const [errorMessage, setErrorMessage] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMoreThanOneOrg, setHasMoreThanOneOrg] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const { authUser, loader } = useSelector(({ auth: userData }) => userData);

  const confirmUser = ({
    password,
    firstName = '',
    lastName = '',
    idCard = '',
    birthDate,
    phone = '',
    address,
    jurisdictionId,
  }) => {
    const auth = new AuthService();
    if (!invitation) {
      setErrorMessage(intl.formatMessage({ id: 'users.invitation.accept.badToken' }));
      return;
    }
    safeExecute(async () => {
      setLoading(true);
      await auth.updateAndSignIn({
        password,
        token: invitation.accessToken,
        organizationId: invitation.orgId,
        user: {
          firstName,
          lastName,
          idCard,
          birthDate,
          phone,
          address,
          jurisdictionId,
        },
      });
    })
      .catch(error => {
        const message =
          typeof error?.message === 'string' && error.message.trim()
            ? error.message
            : intl.formatMessage({ id: 'users.invitation.accept.badToken' });
        setErrorMessage(message);
      })
      .finally(() => setLoading(false));
  };

  const checkUserHasMoreThanOneOrg = async decodedToken => {
    // login with custom token
    const res = await signInWithCustomToken(auth, decodedToken.accessToken);
    // Check if user has previous organizations
    const q = doc(db, FSCollections.USERS, res.user.uid);
    const querySnapshot = await getDoc(q);
    if (querySnapshot.exists()) {
      const userData = querySnapshot.data();
      const userOrgs = Object.keys(userData.organizations);
      setOrgs(userOrgs || []);
      if (userOrgs.length > 1) setHasMoreThanOneOrg(true);
      else setHasMoreThanOneOrg(false);
    }
  };

  // NOTE: We gonna check if the token is valid
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const decodedToken = simpleDecode(token);
      if (
        !decodedToken?.uid ||
        !decodedToken?.orgId ||
        !decodedToken?.accessToken ||
        !decodedToken?.email
      ) {
        setErrorMessage(intl.formatMessage({ id: 'users.invitation.accept.badToken' }));
      } else {
        // Check if user has more than one org
        await checkUserHasMoreThanOneOrg(decodedToken);
        const organizationSnapshot = await getDoc(
          doc(
            db,
            FSCollections.ORGANIZATIONS,
            decodedToken.orgId,
          ),
        );
        setInvitation({
          ...decodedToken,
          organizationCountry: getOrganizationCountry(organizationSnapshot.data()) || '',
        });
        setLoading(false);
      }
    };
    fetchData();
    // Only run effect on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NOTE: When the user is logged in, we are going to clear the local storage
  useEffect(() => {
    if (authUser) {
      clearLocalStorage();
    }
  }, [authUser]);

  return (
    <Card
      footer={
        <div className="gx-px-5">
          <IntlMessages id="users.invitation.accept.footer" />
        </div>
      }
    >
      {!!errorMessage && <Alert description={errorMessage} message="" type="error" />}
      {loading && !errorMessage && (
        <Spin>
          <Alert
            description={<IntlMessages id="users.invitation.accept.footer" />}
            message={<IntlMessages id="users.invitation.accept.loading" />}
            type="info"
          />
        </Spin>
      )}
      {!loading && !errorMessage && (
        <ConfirmUserForm
          email={invitation.email}
          loading={loader || loading}
          onSign={confirmUser}
          firstName={invitation.firstName}
          lastName={invitation.lastName}
          organizationCountry={invitation.organizationCountry}
          skipPassword={hasMoreThanOneOrg}
          orgs={orgs}
        />
      )}
    </Card>
  );
};

export default AcceptInvitation;
